import { Injectable, OnDestroy } from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { pendingChecklist } from '../models/claim-forms';
import { templateByKey } from '../models/communication-templates';
import { COVERAGE_LEVELS, levelIndex, mainCoverages } from '../models/coverage';
import {
  Claim,
  ClaimStatus,
  ClaimThirdParty,
  ClockState,
  Communication,
  DataField,
  DocumentLink,
  DraftKind,
  Endorsement,
  EndorsementType,
  Installment,
  IssuanceRequest,
  IssuanceStatus,
  LineOfBusiness,
  Page,
  Person,
  Policy,
  PolicyDocument,
  ProtoDb,
  ProtoNotification,
  Quote,
  QuoteOffer,
  QuoteSettings,
  RelatedType,
  SessionCounters,
  Ticket,
  TreeInfo,
  TreeMember,
} from '../models/domain.models';
import { ENDING_TYPES, ENDORSEMENT_TYPES } from '../models/endorsement-forms';
import { ENDORSEMENT_TYPE_LABELS, LINE_LABELS } from '../models/labels';
import { PublicComparison } from '../models/public.models';
import {
  CLAIM_ALERT_DAYS,
  CLAIM_REPORT_FAIL_EVERY,
  CLAIM_RESPONSE_DAYS,
  ENDORSEMENT_CONFIRM_MS,
  ENDORSEMENT_REJECT_EVERY,
  MORA_DAYS,
  OFFER_VALID_DAYS,
  PREMIUM_TOLERANCE_PCT,
  SELF_RENEWAL_PREMIUM_RISE_PCT,
  SYNC_STALE_DAYS,
} from '../models/rules';
import { riskLabel, SUM_INSURED_KEY } from '../models/risk-forms';
import {
  AccountView,
  AdminSummary,
  AdminTreesView,
  AgreementsView,
  AuditRow,
  BoardId,
  BoardView,
  CascadePreview,
  CatalogReviewRow,
  CatalogView,
  CommissionLineRow,
  CommissionSummary,
  CompanyAvailability,
  CompanyCard,
  ComplianceFileView,
  ConnectorHealthView,
  DashboardView,
  ImportCompany,
  ImportRunRow,
  ConnectorPeriod,
  CredentialRow,
  CredentialsView,
  DocumentRow,
  DocumentShareRow,
  DraftRow,
  EmissionBookView,
  EndorsementRow,
  EntityOption,
  InstallmentRow,
  InstallmentsSummary,
  InvitationLookup,
  InvitationRow,
  IssuanceContext,
  MemberCard,
  MyCommission,
  PersonLookup,
  PolicyAsOf,
  PolicyExtras,
  PolicyWarning,
  RequoteResult,
  SettlementRow,
  SplitView,
  SupportAccessRow,
  SyncResult,
  TreeChoice,
  TreeNodeRow,
  UploadResult,
  VehicleBrand,
} from '../models/views.models';
import { SessionUser } from '../session/session.models';
import { AccountSaveBody, AuditQuery, CatalogDecision, PlatformEngine, TemplateBody } from './platform-engine';
import { CommissionEngine, ManualSettlementBody } from './commission-engine';
import {
  ComplianceEngine, DraftBody, LegajoFileBody, ShareDocumentsBody, UnusualReportBody, UploadDocumentBody,
} from './compliance-engine';
import { normalizeDocument } from './normalization';
import { failure, Outcome } from './outcome';
import {
  buildContext, draftQuoteRow, matchesFilters, offersValidUntil, ProjectionContext, projectRow, sortRows, summarizeCommissions, summarizeInstallments,
} from './row-projections';
import { PRODUCT_TIER } from './catalog-data';
import { initialVersion, LINE_PREFIX } from './policy-helpers';
import { SessionState, StoreHost } from './store-host';
import { ImportBody, PortfolioEngine, StandalonePolicyBody } from './portfolio-engine';
import { ReportEngine, ReportFilters } from './report-engine';
import { TreeEngine } from './tree-engine';
import { addDays, daysBetween, Rng, todayIso } from './rng';
import { generateSeed, PREMIUM_RANGE } from './seed-generator';
import { COMPANY, DEMO_USERS, MEMBERSHIPS } from './seed-named';
import { VEHICLE_CATALOG } from './vehicle-catalog';

export type CollectionName =
  | 'persons'
  | 'policies'
  | 'quotes'
  | 'claims'
  | 'tickets'
  | 'installments'
  | 'settlements'
  | 'commission-lines'
  | 'notifications'
  | 'issuance-requests'
  | 'endorsements'
  | 'documents'
  | 'communications'
  | 'unusual-reports';

const COLLECTION_KEYS: Record<CollectionName, keyof ProtoDb> = {
  persons: 'persons',
  policies: 'policies',
  quotes: 'quotes',
  claims: 'claims',
  tickets: 'tickets',
  installments: 'installments',
  settlements: 'settlements',
  'commission-lines': 'commissionLines',
  notifications: 'notifications',
  'issuance-requests': 'issuanceRequests',
  endorsements: 'endorsements',
  documents: 'documents',
  communications: 'communications',
  'unusual-reports': 'unusualReports',
};

export function isCollectionName(value: string): value is CollectionName {
  return value in COLLECTION_KEYS;
}

interface Record_ {
  oid: string;
  ownerOid?: string;
  recipientOid?: string;
}

export interface PersonBody {
  legalForm: 'natural' | 'legal';
  documentType: 'DNI' | 'CUIT';
  documentNumber: string;
  displayName: string;
  email?: string;
  phone?: string;
  address?: string;
  birthDate?: string;
  sex?: string;
  activity?: string;
}

export interface LegajoBody {
  activity: string;
  fundsOrigin: string;
  pep: boolean;
  obligedSubjectIsProducer: boolean;
}

export interface CreateQuoteBody {
  lineOfBusiness: LineOfBusiness;
  personOid: string;
  companyOids?: string[];
  risk?: Record<string, string | number>;
  renewalOfPolicyOid?: string;
  draftOid?: string;
}

export interface AdjustOfferBody {
  deductible?: number;
  sumInsured?: number;
}

export interface ShareQuoteBody {
  companyOids: string[];
  message?: string;
}

export interface IssuanceBody {
  quoteOid: string;
  companyOid: string;
  premium?: number;
  data?: Record<string, string | number>;
}

export interface ManualPolicyBody {
  companyOid: string;
  number: string;
  premium: number;
  startDate: string;
  endDate: string;
}

export interface EndorsementBody {
  policyOid: string;
  type: EndorsementType;
  effectDate: string;
  description?: string;
  details?: Record<string, string>;
  issueDate?: string;
  companyNumber?: string;
  premiumDelta?: number;
  documentName?: string;
  affectsMoney?: boolean;
}

export interface ClaimBody {
  policyOid: string;
  incidentDate: string;
  incidentTime?: string;
  type: string;
  affectedItem: string;
  location: string;
  description: string;
  injured: boolean;
  policeReport: boolean;
  thirdParties: ClaimThirdParty[];
  receivedChecklist: string[];
  companyClaimNumber?: string;
  reportedDate?: string;
  draftOid?: string;
}

export interface ClaimStatusBody {
  status: ClaimStatus;
  comment: string;
  date?: string;
}

export interface CommunicationBody {
  personOid: string;
  templateKey: string;
  subject: string;
  body: string;
  attachmentOids: string[];
  relatedType?: RelatedType;
  relatedOid?: string;
}

export interface SelfRenewalBody {
  number?: string;
  premium?: number;
}

export type { Outcome, StoreError } from './outcome';

const ACTIVE_TICKET = ['open', 'in-progress', 'waiting-company'];
const ISSUANCE_SENT_TO_PROCESS_MS = 5000;
const ISSUANCE_TO_OUTCOME_MS = 10000;
const ADJUST_DEDUCTIBLE_STEP = 50000;

const HEALTH: Record<string, Pick<CompanyAvailability, 'health' | 'healthNote'>> = {
  [COMPANY.austral]: { health: 'slow', healthNote: 'Demora alta hoy' },
  [COMPANY.federal]: { health: 'unstable', healthNote: 'Inestable: a veces da timeout' },
};

const DRAFT_LIST_FILTERS = ['status', 'lineOfBusiness', 'personOid', 'ownerOid'];
const INSTALLMENT_TEXTS = ['Contado', 'Hasta 3 cuotas', 'Hasta 6 cuotas', 'Hasta 12 cuotas'];

function riskFromSubject(policy: Policy): Record<string, string | number> {
  if (policy.lineOfBusiness !== 'auto') return { description: policy.subject };
  const match = /^(.*?\s(\d{4}))\s\((.+)\)$/.exec(policy.subject);
  return match ? { vehicle: match[1], vehicleYear: Number(match[2]), plate: match[3] } : { vehicle: policy.subject };
}

function dataField(value: string | undefined): DataField<string> {
  return value ? { state: 'known', value } : { state: 'unreported' };
}

/**
 * In-memory backend of the prototype: one seeded database, a virtual clock and the timers of the
 * asynchronous mock behaviours (M-01 quotes, M-04 issuance). State lives for the page life (D-002-fe).
 * Everything handed out is a copy (see the API router), so signals in screens never alias store objects.
 */
@Injectable({ providedIn: 'root' })
export class ProtoStoreService implements OnDestroy {
  private readonly db: ProtoDb;
  private readonly timers = new Subscription();
  private readonly runtimeRng = new Rng(777);
  private readonly notified = new Set<string>();
  private readonly startDate = todayIso();
  private clockOffsetDays = 0;
  private readonly session: SessionState = { username: null, activeTreeOid: null, support: null, lastOperated: {} };
  private issuanceCounter = 0;
  private requoteCounter = 0;
  private endorsementApiCount = 0;
  private claimApiCount = 0;
  private pampaSyncs = 0;
  private sequence = 1000;
  private readonly tree_: TreeEngine;
  private readonly commissions: CommissionEngine;
  private readonly platform: PlatformEngine;
  private readonly portfolio: PortfolioEngine;
  private readonly reports: ReportEngine;
  private readonly compliance: ComplianceEngine;

  constructor() {
    this.db = generateSeed(this.startDate);
    const host: StoreHost = {
      db: this.db,
      session: this.session,
      today: () => this.today(),
      user: () => this.currentUser(),
      nextSequence: () => ++this.sequence,
      notify: (recipientOid, kind, title, body, relatedType, relatedOid) =>
        this.notify(recipientOid, kind, title, body, relatedType, relatedOid),
    };
    this.tree_ = new TreeEngine(host);
    this.commissions = new CommissionEngine(host);
    this.platform = new PlatformEngine(host, this.tree_);
    this.portfolio = new PortfolioEngine(host, this.tree_);
    this.reports = new ReportEngine(host);
    this.compliance = new ComplianceEngine(host);
    this.session.lastOperated = {
      'prod:tree-rios': addDays(this.startDate, -1),
      'prod:tree-federal': addDays(this.startDate, -9),
    };
    for (const quote of this.db.quotes.filter(q => q.status === 'quoting')) {
      quote.offers.filter(o => o.status === 'pending').forEach(offer => this.scheduleOffer(quote, offer));
    }
  }

  ngOnDestroy(): void {
    this.timers.unsubscribe();
  }

  // ── Session ───────────────────────────────────────────────────

  signIn(username: string): SessionUser | null {
    const user = DEMO_USERS.find(u => u.username === username.trim().toLowerCase());
    this.session.username = user?.username ?? null;
    this.session.support = null;
    const memberships = MEMBERSHIPS[user?.username ?? ''] ?? [];
    this.session.activeTreeOid = memberships.length === 1 ? memberships[0].treeOid : null;
    return this.currentUser();
  }

  signOut(): void {
    this.session.username = null;
    this.session.activeTreeOid = null;
    this.session.support = null;
  }

  /**
   * The demo user as the tree gives them now: the node they operate as in the active tree, a seller's emit
   * capability (edited in P-81), or, in support mode, the root producer of the tree being looked at.
   */
  currentUser(): SessionUser | null {
    const demo = DEMO_USERS.find(u => u.username === this.session.username);
    if (!demo) return null;
    const support = this.session.support;
    if (support) {
      const tree = this.treeInfo(support.treeOid);
      return {
        ...demo, role: 'producer', canEmit: true, memberOid: tree.rootOid, treeOid: tree.oid, treeName: tree.name,
        treeCount: 0, treePending: false, support: { accessOid: support.accessOid, treeOid: tree.oid, treeName: tree.name, reason: support.reason },
      };
    }
    const memberships = MEMBERSHIPS[demo.username] ?? [];
    if (memberships.length === 0) return { ...demo };
    const active = memberships.find(m => m.treeOid === this.session.activeTreeOid);
    if (!active) {
      return { ...demo, memberOid: null, treeOid: null, treeName: null, treeCount: memberships.length, treePending: true };
    }
    const member = this.db.members.find(m => m.oid === active.memberOid);
    return {
      ...demo, memberOid: active.memberOid, treeOid: active.treeOid, treeName: this.treeInfo(active.treeOid).name,
      canEmit: member?.canEmit ?? demo.canEmit, treeCount: memberships.length, treePending: false,
    };
  }

  /** Refusal message for a login (suspended in the only tree, or in all of them), or null. */
  loginBlock(username: string): string | null {
    return this.platform.loginBlock(username.trim().toLowerCase());
  }

  treeChoices(): TreeChoice[] {
    return this.platform.treeChoices(this.session.username ?? '');
  }

  selectTree(treeOid: string): Outcome<SessionUser> {
    const entered = this.platform.enterTree(this.session.username ?? '', treeOid);
    return 'error' in entered ? entered : { value: this.currentUser()! };
  }

  /** Support mode is read-only: the router asks here before any non-GET request reaches the store. */
  writeBlockedMessage(method: string, path: string): string | null {
    if (!this.session.support || method === 'GET' || path === 'support/exit') return null;
    return PlatformEngine.readOnlyMessage();
  }

  enterSupport(treeOid: string, reason: string): Outcome<SessionUser> {
    const entered = this.platform.enterSupport(treeOid, reason);
    return 'error' in entered ? entered : { value: this.currentUser()! };
  }

  exitSupport(): Outcome<SessionUser> {
    const left = this.platform.exitSupport();
    return 'error' in left ? left : { value: this.currentUser()! };
  }

  // ── Platform: back-office, audit, settings ────────────────────

  adminSummary(): Outcome<AdminSummary> {
    return this.platform.adminSummary();
  }

  adminTrees(): Outcome<AdminTreesView> {
    return this.platform.adminTrees();
  }

  adminCascade(oid: string, action: string): Outcome<CascadePreview> {
    return this.platform.adminCascade(oid, action);
  }

  adminMemberAction(oid: string, action: 'suspend' | 'reactivate' | 'baja'): Outcome<AdminTreesView> {
    return this.platform.adminMemberAction(oid, action);
  }

  catalogReviews(): Outcome<CatalogReviewRow[]> {
    return this.platform.catalogReviews();
  }

  confirmCatalog(oid: string, body: CatalogDecision): Outcome<CatalogReviewRow> {
    return this.platform.confirmCatalog(oid, body);
  }

  discontinueCatalog(oid: string): Outcome<CatalogReviewRow> {
    return this.platform.discontinueCatalog(oid);
  }

  connectorHealth(period: ConnectorPeriod): Outcome<ConnectorHealthView> {
    return this.platform.connectorHealth(period);
  }

  myAudit(query: AuditQuery, page: number, size: number): Page<AuditRow> {
    return this.platform.myAudit(query, page, size);
  }

  myAuditUsers(): string[] {
    return this.platform.myAuditUsers();
  }

  mySupportAccesses(): SupportAccessRow[] {
    return this.platform.mySupportAccesses();
  }

  globalAudit(query: AuditQuery, page: number, size: number): Outcome<Page<AuditRow>> {
    return this.platform.globalAudit(query, page, size);
  }

  globalAuditUsers(): string[] {
    return this.platform.globalAuditUsers();
  }

  accountView(): Outcome<AccountView> {
    return this.platform.accountView();
  }

  saveAccount(body: AccountSaveBody): Outcome<AccountView> {
    return this.platform.saveAccount(body);
  }

  setTwoFactor(enabled: boolean, code: string): Outcome<AccountView> {
    return this.platform.setTwoFactor(enabled, code);
  }

  changePassword(current: string, next: string): Outcome<true> {
    return this.platform.changePassword(current, next);
  }

  saveTemplate(body: TemplateBody): Outcome<AccountView> {
    return this.platform.saveTemplate(body);
  }

  deleteTemplate(oid: string): Outcome<AccountView> {
    return this.platform.deleteTemplate(oid);
  }

  quoteSettings(): Outcome<QuoteSettings> {
    return this.platform.quoteSettings();
  }

  saveQuoteSettings(body: QuoteSettings): Outcome<QuoteSettings> {
    return this.platform.saveQuoteSettings(body);
  }

  private treeInfo(oid: string): TreeInfo {
    return this.db.trees.find(t => t.oid === oid) ?? this.db.trees[0];
  }

  // ── Clock (M-10) ──────────────────────────────────────────────

  today(): string {
    return addDays(this.startDate, this.clockOffsetDays);
  }

  clock(): ClockState {
    return { today: this.today(), offsetDays: this.clockOffsetDays };
  }

  advanceClock(days: number): ClockState {
    this.clockOffsetDays += days;
    this.fireTimeBasedEvents();
    return this.clock();
  }

  // ── Reads ─────────────────────────────────────────────────────

  tree(): { tree: TreeInfo; members: TreeMember[] } {
    const treeOid = this.currentUser()?.treeOid ?? this.db.trees[0].oid;
    return { tree: this.treeInfo(treeOid), members: this.db.members.filter(m => m.treeOid === treeOid) };
  }

  companies(): ProtoDb['companies'] {
    return this.db.companies;
  }

  vehicleCatalog(): VehicleBrand[] {
    return VEHICLE_CATALOG;
  }

  counters(): SessionCounters {
    const memberOid = this.currentUser()?.memberOid;
    if (!memberOid) return { unreadNotifications: 0, trayCount: 0 };
    const limit = addDays(this.today(), 3);
    return {
      unreadNotifications: this.db.notifications.filter(n => n.recipientOid === memberOid && !n.read).length,
      trayCount: this.db.tickets.filter(
        t => t.category === 'task' && t.assigneeOid === memberOid && ACTIVE_TICKET.includes(t.status)
          && t.dueDate !== null && t.dueDate <= limit,
      ).length,
    };
  }

  list(name: CollectionName, page: number, size: number, filter: string, filters: Record<string, string> = {}): Page<unknown> {
    const ctx = this.context();
    const rows = [...this.visible(name).map(item => projectRow(name, item, ctx)), ...this.draftRows(name, filters, ctx)]
      .filter(row => matchesFilters(name, row, filters, ctx))
      .filter(row => !filter || JSON.stringify(row).toLowerCase().includes(filter));
    const sorted = sortRows(name, rows);
    return { data: sorted.slice((page - 1) * size, page * size), total: sorted.length };
  }

  private draftRows(name: CollectionName, filters: Record<string, string>, ctx: ProjectionContext): unknown[] {
    const memberOid = this.currentUser()?.memberOid;
    if (name !== 'quotes' || !memberOid || !Object.keys(filters).every(key => DRAFT_LIST_FILTERS.includes(key))) return [];
    return this.db.drafts.filter(d => d.kind === 'quote' && d.ownerOid === memberOid).map(d => draftQuoteRow(d, ctx));
  }

  find(name: CollectionName, oid: string): unknown {
    const item = this.visible(name).find(i => i.oid === oid);
    return item ? projectRow(name, item, this.context()) : null;
  }

  installmentsSummary(): InstallmentsSummary {
    const ctx = this.context();
    const rows = this.visible('installments').map(i => projectRow('installments', i, ctx) as InstallmentRow);
    return summarizeInstallments(rows, this.visible('policies') as unknown as Policy[]);
  }

  markNotificationRead(oid: string): ProtoNotification | null {
    const found = this.visible('notifications').find(n => n.oid === oid) as ProtoNotification | undefined;
    if (found) found.read = true;
    return found ?? null;
  }

  markAllNotificationsRead(): number {
    const unread = this.visible('notifications').filter(n => !(n as ProtoNotification).read) as ProtoNotification[];
    unread.forEach(n => { n.read = true; });
    return unread.length;
  }

  // ── Commissions (P-65 … P-68) ─────────────────────────────────

  commissionSummary(filters: Record<string, string>): CommissionSummary {
    const ctx = this.context();
    const rows = this.visible('commission-lines')
      .map(item => projectRow('commission-lines', item, ctx) as CommissionLineRow)
      .filter(row => matchesFilters('commission-lines', row, filters, ctx));
    return summarizeCommissions(rows);
  }

  defaultCommissionPeriod(): string {
    return this.commissions.defaultPeriod();
  }

  agreementsView(): AgreementsView {
    return this.commissions.agreementsView();
  }

  saveAgreement(body: Parameters<CommissionEngine['saveAgreement']>[0]): Outcome<AgreementsView> {
    const saved = this.commissions.saveAgreement(body);
    return 'error' in saved ? saved : { value: this.commissions.agreementsView() };
  }

  splitView(period: string): SplitView {
    return this.commissions.splitView(period);
  }

  saveSplit(body: Parameters<CommissionEngine['saveSplit']>[0]): Outcome<SplitView> {
    const saved = this.commissions.saveSplit(body);
    return 'error' in saved ? saved : { value: this.commissions.splitView(this.commissions.defaultPeriod()) };
  }

  myCommission(period: string): MyCommission {
    return this.commissions.myCommission(period);
  }

  uploadSettlement(body: { companyOid: string; period: string; fileName: string }): Outcome<UploadResult> {
    return this.commissions.uploadSettlement(body);
  }

  manualSettlement(body: ManualSettlementBody): Outcome<SettlementRow> {
    const saved = this.commissions.manualSettlement(body);
    return 'error' in saved ? saved : { value: this.find('settlements', saved.value.oid) as SettlementRow };
  }

  generateClaim(lineOid: string): Outcome<CommissionLineRow> {
    const saved = this.commissions.generateClaim(lineOid);
    return 'error' in saved ? saved : { value: this.find('commission-lines', saved.value.oid) as CommissionLineRow };
  }

  // ── Portfolio and reports (P-01, P-43, P-46, P-75, P-84, P-85) ─

  dashboard(filters: ReportFilters): Outcome<DashboardView> {
    const view = this.reports.dashboard(filters);
    return view ? { value: view } : failure(403, 'FORBIDDEN', 'El inicio es para productores y vendedores');
  }

  board(id: BoardId, filters: ReportFilters): Outcome<BoardView> {
    const view = this.reports.board(id, filters);
    return view ? { value: view } : failure(404, 'NOT_FOUND', 'Tablero inexistente o sin acceso para tu rol');
  }

  companiesView(): CompanyCard[] {
    return this.portfolio.companiesView();
  }

  catalogView(): CatalogView {
    return this.portfolio.catalogView();
  }

  setProductHidden(key: string, hide: boolean): Outcome<CatalogView> {
    return this.portfolio.setProductHidden(key, hide);
  }

  importCompanies(): ImportCompany[] {
    return this.portfolio.importCompanies();
  }

  importRuns(): ImportRunRow[] {
    return this.portfolio.importRuns();
  }

  runImport(body: ImportBody): Outcome<ImportRunRow> {
    return this.portfolio.runImport(body);
  }

  undoImport(oid: string): Outcome<ImportRunRow> {
    return this.portfolio.undoImport(oid);
  }

  registerStandalonePolicy(body: StandalonePolicyBody): Outcome<Policy> {
    return this.portfolio.registerStandalonePolicy(body);
  }

  // ── Documents, compliance and gaps (P-70, E-02, P-23, P-86, P-87, drafts) ─

  documentEntities(type: DocumentLink['type'], filter: string): EntityOption[] {
    return this.compliance.entityOptions(type, filter);
  }

  uploadDocument(body: UploadDocumentBody): Outcome<DocumentRow> {
    const saved = this.compliance.uploadDocument(body);
    return 'error' in saved ? saved : { value: this.find('documents', saved.value.oid) as DocumentRow };
  }

  associateDocument(oid: string, link: DocumentLink): Outcome<DocumentRow> {
    const saved = this.compliance.associateDocument(oid, link);
    return 'error' in saved ? saved : { value: this.find('documents', saved.value.oid) as DocumentRow };
  }

  shareDocuments(body: ShareDocumentsBody): Outcome<DocumentShareRow> {
    return this.compliance.createShare(body);
  }

  documentShares(): DocumentShareRow[] {
    return this.compliance.shares();
  }

  publicDocuments(token: string) {
    return this.compliance.publicDocuments(token);
  }

  legajoView(personOid: string): Outcome<ComplianceFileView> {
    return this.compliance.legajoView(personOid);
  }

  saveComplianceFile(personOid: string, body: LegajoFileBody): Outcome<ComplianceFileView> {
    return this.compliance.saveLegajo(personOid, body);
  }

  createUnusualReport(body: UnusualReportBody): Outcome<unknown> {
    const saved = this.compliance.createUnusual(body);
    return 'error' in saved ? saved : { value: this.find('unusual-reports', saved.value.oid) };
  }

  updateUnusualReport(oid: string, body: UnusualReportBody): Outcome<unknown> {
    const saved = this.compliance.updateUnusual(oid, body);
    return 'error' in saved ? saved : { value: this.find('unusual-reports', saved.value.oid) };
  }

  prepareUnusualReport(oid: string): Outcome<unknown> {
    const saved = this.compliance.prepareUnusual(oid);
    return 'error' in saved ? saved : { value: this.find('unusual-reports', saved.value.oid) };
  }

  fileUnusualReport(oid: string, filedDate: string, receiptNumber: string): Outcome<unknown> {
    const saved = this.compliance.fileUnusual(oid, filedDate, receiptNumber);
    return 'error' in saved ? saved : { value: this.find('unusual-reports', saved.value.oid) };
  }

  emissionBook(from: string, to: string): Outcome<EmissionBookView> {
    return this.compliance.emissionBook(from, to);
  }

  generateEmissionBook(from: string, to: string): Outcome<EmissionBookView> {
    return this.compliance.generateEmissionBook(from, to);
  }

  policyExtras(oid: string): Outcome<PolicyExtras> {
    return this.compliance.policyExtras(oid);
  }

  saveDraft(body: DraftBody): Outcome<DraftRow> {
    return this.compliance.saveDraft(body);
  }

  drafts(kind: DraftKind | null): DraftRow[] {
    return this.compliance.drafts(kind);
  }

  draft(oid: string): Outcome<DraftRow> {
    return this.compliance.draft(oid);
  }

  deleteDraft(oid: string): Outcome<{ deleted: true }> {
    return this.compliance.deleteDraft(oid);
  }

  // ── Tree (P-80 … P-83) ─────────────────────────────────────────

  treeNodes(): TreeNodeRow[] {
    return this.tree_.nodes();
  }

  memberCard(oid: string): Outcome<MemberCard> {
    return this.tree_.card(oid);
  }

  editMember(oid: string, body: Parameters<TreeEngine['editMember']>[1]): Outcome<MemberCard> {
    return this.tree_.editMember(oid, body);
  }

  memberCascade(oid: string): Outcome<CascadePreview> {
    return this.tree_.cascade(oid);
  }

  suspendMember(oid: string): Outcome<MemberCard> {
    return this.tree_.suspend(oid);
  }

  reactivateMember(oid: string): Outcome<MemberCard> {
    return this.tree_.reactivate(oid);
  }

  setMemberCanEmit(oid: string, canEmit: boolean): Outcome<MemberCard> {
    return this.tree_.setCanEmit(oid, canEmit);
  }

  saveOwnCodes(oid: string, codes: Record<string, string>): Outcome<MemberCard> {
    return this.tree_.saveOwnCodes(oid, codes);
  }

  createSeller(body: Parameters<TreeEngine['createSeller']>[0]): Outcome<MemberCard> {
    return this.tree_.createSeller(body);
  }

  invitations(): InvitationRow[] {
    return this.tree_.invitations();
  }

  lookupInvitation(email: string, license: string, taxId: string): InvitationLookup {
    return this.tree_.lookupInvitation(email, license, taxId);
  }

  invite(body: Parameters<TreeEngine['invite']>[0]): Outcome<InvitationRow> {
    return this.tree_.invite(body);
  }

  resendInvitation(oid: string): Outcome<InvitationRow> {
    return this.tree_.resendInvitation(oid);
  }

  resendInvitationOfMember(memberOid: string): Outcome<MemberCard> {
    return this.tree_.resendForMember(memberOid);
  }

  acceptInvitation(oid: string): Outcome<InvitationRow> {
    return this.tree_.acceptInvitation(oid);
  }

  credentialsView(memberOid: string): Outcome<CredentialsView> {
    return this.tree_.credentialsView(memberOid);
  }

  saveCredential(memberOid: string, companyOid: string, body: Parameters<TreeEngine['saveCredential']>[2]): Outcome<CredentialRow> {
    return this.tree_.saveCredential(memberOid, companyOid, body);
  }

  setCredentialLines(memberOid: string, companyOid: string, lines: LineOfBusiness[]): Outcome<CredentialRow> {
    return this.tree_.setLines(memberOid, companyOid, lines);
  }

  validateCredential(memberOid: string, companyOid: string): Outcome<CredentialRow> {
    return this.tree_.validateCredential(memberOid, companyOid);
  }

  deleteCredential(memberOid: string, companyOid: string): Outcome<CredentialRow> {
    return this.tree_.deleteCredential(memberOid, companyOid);
  }

  private context(): ProjectionContext {
    return buildContext(this.db, this.today(), this.currentUser());
  }

  // ── Persons (P-22) and the normalization service (M-05) ───────

  lookupPerson(documentNumber: string): PersonLookup {
    const digits = documentNumber.replace(/\D/g, '');
    const existing = this.db.persons.find(p => p.documentNumber === digits);
    const accessible = existing ? this.visible('persons').some(p => p.oid === existing.oid) : false;
    return {
      existing: existing
        ? { oid: existing.oid, displayName: existing.displayName, kind: existing.kind, accessible }
        : null,
      normalized: existing ? null : normalizeDocument(digits),
    };
  }

  createPerson(body: PersonBody): Person | null {
    const user = this.currentUser();
    if (!user?.memberOid) return null;
    const documentNumber = body.documentNumber.replace(/\D/g, '');
    if (this.db.persons.some(p => p.documentNumber === documentNumber)) return null;
    const person: Person = {
      oid: `per-${++this.sequence}`, kind: 'lead', legalForm: body.legalForm,
      displayName: body.displayName.trim(), documentType: body.documentType, documentNumber,
      email: dataField(body.email), phone: dataField(body.phone),
      address: body.address ? dataField(body.address) : { state: 'pending' },
      ownerOid: user.memberOid, createDate: this.today(),
      birthDate: body.birthDate || undefined, sex: body.sex || undefined, activity: body.activity || undefined,
    };
    this.db.persons.unshift(person);
    return person;
  }

  updatePerson(oid: string, body: PersonBody): Person | null {
    const person = this.visible('persons').find(p => p.oid === oid) as Person | undefined;
    if (!person) return null;
    person.legalForm = body.legalForm;
    person.displayName = body.displayName.trim();
    person.email = dataField(body.email);
    person.phone = dataField(body.phone);
    person.address = body.address ? dataField(body.address) : { state: 'pending' };
    person.birthDate = body.birthDate || undefined;
    person.sex = body.sex || undefined;
    person.activity = body.activity || undefined;
    return person;
  }

  saveLegajo(oid: string, body: LegajoBody): Person | null {
    const person = this.visible('persons').find(p => p.oid === oid) as Person | undefined;
    if (!person) return null;
    person.legajo = {
      activity: body.activity, fundsOrigin: body.fundsOrigin, pep: body.pep,
      obligedSubjectIsProducer: body.obligedSubjectIsProducer,
      updateDate: this.today(),
      state: body.activity && body.fundsOrigin && body.obligedSubjectIsProducer ? 'complete' : 'incomplete',
    };
    return person;
  }

  // ── Quotes (P-31) ─────────────────────────────────────────────

  quotingCompanies(line: LineOfBusiness): CompanyAvailability[] {
    const member = this.db.members.find(m => m.oid === this.currentUser()?.memberOid);
    const holderOid = member ? this.licenseHolderFor(member).oid : '';
    return this.db.companies.filter(c => c.lines.includes(line)).map(c => {
      const availability = this.tree_.availability(holderOid, c.oid, line);
      return {
        oid: c.oid, name: c.name, quoteMode: c.capabilities.quote, issueMode: c.capabilities.issue,
        health: HEALTH[c.oid]?.health ?? 'ok', healthNote: HEALTH[c.oid]?.healthNote ?? null,
        available: availability.ok, unavailableReason: availability.reason,
      };
    });
  }

  // ── M-01: progressive quotes ──────────────────────────────────

  createQuote(body: CreateQuoteBody): Quote | null {
    const user = this.currentUser();
    const person = (this.visible('persons') as unknown as Person[]).find(p => p.oid === body.personOid);
    if (!user?.memberOid || !person) return null;
    const allowed = new Set(this.quotingCompanies(body.lineOfBusiness).filter(c => c.available).map(c => c.oid));
    const candidates = this.db.companies.filter(
      c => allowed.has(c.oid) && (!body.companyOids || body.companyOids.includes(c.oid)),
    );
    if (candidates.length === 0) return null;
    const quote: Quote = {
      oid: `quo-${++this.sequence}`,
      number: `COT-${String(this.sequence).padStart(5, '0')}`,
      lineOfBusiness: body.lineOfBusiness,
      personOid: body.personOid,
      ownerOid: user.memberOid,
      status: 'quoting',
      createDate: this.today(),
      risk: body.risk ?? {},
      offers: candidates.map(c => this.pendingOffer(c.oid, 1)),
      version: 1, previousVersions: [], chosenCompanyOid: null, share: null, clientChoice: null, desistReason: null,
      renewalOfPolicyOid: body.renewalOfPolicyOid,
    };
    this.db.quotes.unshift(quote);
    this.ensureSaleTicket(quote, user.memberOid);
    const previous = this.db.policies.find(p => p.oid === body.renewalOfPolicyOid);
    this.log(quote.oid, previous
      ? `Renovación de la póliza ${previous.number}: cotización creada con ${candidates.length} compañías`
      : `Cotización ${quote.number} creada con ${candidates.length} compañías`);
    quote.offers.forEach(offer => this.scheduleOffer(quote, offer));
    this.compliance.consumeDraft(body.draftOid, 'quote');
    return quote;
  }

  retryOffer(quoteOid: string, companyOid: string): Quote | null {
    const quote = this.quoteFor(quoteOid);
    const offer = quote?.offers.find(o => o.companyOid === companyOid);
    if (!quote || !offer) return null;
    offer.attempt++;
    offer.status = 'pending';
    offer.message = null;
    offer.companyMessage = null;
    offer.errorCategory = null;
    if (quote.status === 'presented') quote.status = 'quoting';
    this.log(quote.oid, `Reintento manual con ${this.companyName(companyOid)}`);
    this.scheduleOffer(quote, offer);
    return quote;
  }

  recotizeQuote(quoteOid: string): Quote | null {
    const quote = this.quoteFor(quoteOid);
    if (!quote || quote.status === 'issued' || quote.status === 'desisted') return null;
    quote.previousVersions.push({
      version: quote.version, createDate: this.today(), offers: structuredClone(quote.offers),
    });
    quote.version++;
    quote.offers = quote.offers.map(o => this.pendingOffer(o.companyOid, o.attempt + 1));
    quote.chosenCompanyOid = null;
    quote.status = 'quoting';
    this.log(quote.oid, `Recotizada: versión ${quote.version}`);
    quote.offers.forEach(offer => this.scheduleOffer(quote, offer));
    return quote;
  }

  // ── M-02: adjusting one offer ─────────────────────────────────

  adjustOffer(quoteOid: string, companyOid: string, body: AdjustOfferBody): Quote | null {
    const quote = this.quoteFor(quoteOid);
    const offer = quote?.offers.find(o => o.companyOid === companyOid && o.status === 'received');
    if (!quote || !offer || offer.premium === null) return null;
    offer.originalPremium ??= offer.premium;
    offer.originalDeductible ??= offer.deductible;
    let factor = 1;
    if (body.deductible !== undefined && offer.deductible !== null && body.deductible !== offer.deductible) {
      const pct = this.runtimeRng.int(5, 15) / 100;
      factor *= body.deductible > offer.deductible ? 1 - pct : 1 + pct;
      offer.deductible = body.deductible;
    }
    if (body.sumInsured !== undefined && offer.sumInsured && body.sumInsured !== offer.sumInsured) {
      const pct = this.runtimeRng.int(3, 8) / 100;
      factor *= body.sumInsured > offer.sumInsured ? 1 + pct : 1 - pct;
      offer.sumInsured = body.sumInsured;
    }
    offer.premium = Math.round((offer.premium * factor) / 100) * 100;
    offer.adjusted = true;
    this.log(quote.oid, `Oferta de ${this.companyName(companyOid)} ajustada: nuevo premio ${offer.premium}`);
    return quote;
  }

  chooseOffer(quoteOid: string, companyOid: string | null): Quote | null {
    const quote = this.quoteFor(quoteOid);
    if (!quote || ['issued', 'desisted'].includes(quote.status)) return null;
    if (companyOid !== null && !quote.offers.some(o => o.companyOid === companyOid && o.status === 'received')) return null;
    quote.chosenCompanyOid = companyOid;
    if (companyOid) {
      quote.status = 'chosen-by-client';
      this.log(quote.oid, `Oferta de ${this.companyName(companyOid)} marcada como elegida por el cliente`);
    } else {
      quote.status = quote.share ? 'presented' : 'quoting';
      this.log(quote.oid, 'Se quitó la oferta elegida');
    }
    return quote;
  }

  desistQuote(quoteOid: string, reason: string): Quote | null {
    const quote = this.quoteFor(quoteOid);
    if (!quote || ['issued', 'desisted'].includes(quote.status)) return null;
    quote.status = 'desisted';
    quote.desistReason = reason.trim() || null;
    this.closeTicket(quote, quote.desistReason ? `Cotización desistida: ${quote.desistReason}` : 'Cotización desistida');
    return quote;
  }

  handOverQuote(quoteOid: string): Quote | null {
    const user = this.currentUser();
    const quote = this.quoteFor(quoteOid);
    const parentOid = this.db.members.find(m => m.oid === user?.memberOid)?.parentOid;
    if (!user || !quote || !parentOid || ['issued', 'desisted'].includes(quote.status)) return null;
    const ticket = this.ensureSaleTicket(quote, quote.ownerOid);
    ticket.assigneeOid = parentOid;
    ticket.category = 'task';
    ticket.status = 'open';
    ticket.dueDate = addDays(this.today(), 2);
    ticket.title = `Emitir ${quote.number}`;
    const parentName = this.memberName(parentOid);
    this.log(quote.oid, `${user.nombre} ${user.apellido} pasó la venta a ${parentName} para emitir`);
    this.notify(parentOid, 'issuance', `${user.nombre} ${user.apellido} te pasó una venta para emitir`,
      `Cotización ${quote.number}: abrila y emití con tu matrícula.`, 'quote', quote.oid);
    return quote;
  }

  // ── Sharing (P-33) and the public comparison (E-01, M-11) ─────

  shareQuote(quoteOid: string, body: ShareQuoteBody): Quote | null {
    const quote = this.quoteFor(quoteOid);
    if (!quote || ['issued', 'desisted'].includes(quote.status)) return null;
    const included = quote.offers.filter(o => o.status === 'received' && body.companyOids.includes(o.companyOid));
    if (included.length === 0) return null;
    quote.share = {
      token: quote.share?.token ?? crypto.randomUUID().replace(/-/g, '').slice(0, 20),
      createDate: this.today(),
      expiryDate: offersValidUntil(quote) ?? addDays(this.today(), OFFER_VALID_DAYS),
      companyOids: included.map(o => o.companyOid),
      message: body.message?.trim() ?? '',
      opened: quote.share?.opened ?? false,
      openedDate: quote.share?.openedDate ?? null,
    };
    if (quote.status === 'quoting' || quote.status === 'draft') quote.status = 'presented';
    this.log(quote.oid, `Comparativo compartido con ${included.length} ofertas`);
    return quote;
  }

  publicComparison(token: string): PublicComparison | null {
    const quote = this.db.quotes.find(q => q.share?.token === token);
    if (!quote?.share) return null;
    if (!quote.share.opened) {
      quote.share.opened = true;
      quote.share.openedDate = this.today();
      this.log(quote.oid, 'El cliente abrió el comparativo');
    }
    return this.toPublic(quote);
  }

  choosePublicOffer(token: string, companyOid: string, message: string): PublicComparison | null {
    const quote = this.db.quotes.find(q => q.share?.token === token);
    if (!quote?.share) return null;
    const expired = quote.share.expiryDate < this.today();
    const offer = quote.offers.find(o => o.companyOid === companyOid && o.status === 'received');
    if (!expired && offer && quote.share.companyOids.includes(companyOid)) {
      quote.clientChoice = { companyOid, message: message.trim(), date: this.today() };
      const person = this.db.persons.find(p => p.oid === quote.personOid);
      const company = this.companyName(companyOid);
      this.log(quote.oid, `El cliente eligió la opción de ${company}`);
      const owner = this.db.members.find(m => m.oid === quote.ownerOid);
      const recipients = new Set([quote.ownerOid, owner?.parentOid ?? quote.ownerOid]);
      recipients.forEach(oid => this.notify(oid, 'client-choice', `${person?.displayName ?? 'El cliente'} eligió una opción`,
        `Cotización ${quote.number}: eligió ${company}.${message ? ` Mensaje: ${message}` : ''}`, 'quote', quote.oid));
    }
    return this.toPublic(quote);
  }

  private toPublic(quote: Quote): PublicComparison {
    const share = quote.share!;
    const owner = this.db.members.find(m => m.oid === quote.ownerOid);
    const producer = (owner?.kind === 'seller' ? this.db.members.find(m => m.oid === owner.parentOid) : owner) ?? owner;
    const person = this.db.persons.find(p => p.oid === quote.personOid);
    const expired = share.expiryDate < this.today();
    const line = quote.lineOfBusiness;
    return {
      expired,
      today: this.today(),
      validUntil: share.expiryDate,
      producer: {
        brand: this.treeInfo(owner?.treeOid ?? '').name, name: producer?.name ?? '—', license: producer?.license ?? null,
        email: producer?.email ?? '', phone: producer?.phone ?? null,
      },
      clientName: person?.displayName ?? '',
      lineOfBusiness: line,
      riskSummary: expired ? '' : String(quote.risk['vehicle'] ?? quote.risk['description'] ?? ''),
      riskDetails: expired ? [] : Object.entries(quote.risk)
        .filter(([key]) => key !== 'description' && key !== 'plate')
        .map(([key, value]) => ({ label: riskLabel(line, key), value: String(value) })),
      message: share.message,
      offers: expired ? [] : quote.offers
        .filter(o => o.status === 'received' && share.companyOids.includes(o.companyOid))
        .map(o => ({
          companyOid: o.companyOid, companyName: this.companyName(o.companyOid), product: o.product ?? '',
          planName: o.planName ?? '', level: o.coverageLevel ?? '', premium: o.premium ?? 0,
          deductible: o.deductible, sumInsured: o.sumInsured ?? null, installmentsText: o.installmentsText ?? '',
          mainCoverages: mainCoverages(line, o.coverageLevel),
          validUntil: addDays(o.respondedAt ?? share.createDate, OFFER_VALID_DAYS),
        })),
      chosenCompanyOid: quote.clientChoice?.companyOid ?? null,
    };
  }

  // ── M-03 / M-04: re-quote and issuance that advances by itself ─

  requote(quoteOid: string, companyOid: string): RequoteResult | null {
    const quote = this.quoteFor(quoteOid);
    const offer = quote?.offers.find(o => o.companyOid === companyOid && o.status === 'received');
    if (!quote || !offer || offer.premium === null) return null;
    const slot = this.requoteCounter++ % 10;
    let changePct = 0;
    let outcome: RequoteResult['outcome'] = 'unchanged';
    if (slot === 9) {
      changePct = this.runtimeRng.int(40, 90) / 10;
      outcome = 'above-tolerance';
    } else if (slot >= 7) {
      changePct = (this.runtimeRng.chance(0.5) ? 1 : -1) * (this.runtimeRng.int(5, 28) / 10);
      outcome = 'within-tolerance';
    }
    const newPremium = Math.round((offer.premium * (1 + changePct / 100)) / 100) * 100;
    return {
      companyOid, previousPremium: offer.premium, newPremium, changePct,
      tolerancePct: PREMIUM_TOLERANCE_PCT, outcome, requiresConfirmation: outcome === 'above-tolerance',
    };
  }

  issuanceContext(quoteOid: string, companyOid: string): IssuanceContext | null {
    const user = this.currentUser();
    const quote = this.quoteFor(quoteOid);
    const member = this.db.members.find(m => m.oid === user?.memberOid);
    const company = this.db.companies.find(c => c.oid === companyOid);
    if (!quote || !member || !company) return null;
    const holder = this.licenseHolderFor(member);
    const owner = this.db.members.find(m => m.oid === quote.ownerOid) ?? member;
    const person = this.db.persons.find(p => p.oid === quote.personOid);
    const availability = this.tree_.availability(holder.oid, company.oid, quote.lineOfBusiness);
    const credential = this.db.credentials.find(c => c.memberOid === holder.oid && c.companyOid === company.oid);
    return {
      credentialsOk: availability.ok,
      credentialsReason: availability.reason,
      licenseHolder: { oid: holder.oid, name: holder.name, license: holder.license },
      attributedTo: { oid: owner.oid, name: owner.name },
      producerCode: member.ownCodes[company.oid] ?? credential?.producerCode ?? `${company.oid.slice(4).toUpperCase()}-${holder.license?.slice(4) ?? '000'}`,
      issuesByApi: company.capabilities.issue === 'api',
      legajo: person?.legajo ?? null,
    };
  }

  createIssuance(body: IssuanceBody): IssuanceRequest | null {
    const user = this.currentUser();
    const quote = this.quoteFor(body.quoteOid);
    const company = this.db.companies.find(c => c.oid === body.companyOid);
    const offer = quote?.offers.find(o => o.companyOid === body.companyOid && o.status === 'received');
    const member = this.db.members.find(m => m.oid === user?.memberOid);
    if (!user?.memberOid || !quote || !company || !offer || !member) return null;
    if (company.capabilities.issue !== 'api' || ['issued', 'desisted'].includes(quote.status)) return null;
    if (!this.tree_.availability(this.licenseHolderFor(member).oid, company.oid, quote.lineOfBusiness).ok) return null;
    const n = ++this.issuanceCounter;
    const request: IssuanceRequest = {
      oid: `iss-${++this.sequence}`,
      number: `SOL-${String(this.sequence).padStart(5, '0')}`,
      quoteOid: quote.oid, companyOid: company.oid,
      ownerOid: quote.ownerOid,
      requestedByOid: user.memberOid,
      licenseHolderOid: this.licenseHolderFor(member).oid,
      status: 'sent',
      createDate: this.today(),
      policyOid: null,
      premium: body.premium ?? offer.premium ?? 0,
      data: body.data ?? {},
      history: [],
      rejectionReason: null,
    };
    this.advance(request, 'sent', `Solicitud enviada a ${company.name}.`);
    quote.chosenCompanyOid = company.oid;
    quote.status = 'accepted';
    this.log(quote.oid, `Solicitud de emisión ${request.number} enviada a ${company.name}`);
    this.db.issuanceRequests.unshift(request);
    this.timers.add(timer(ISSUANCE_SENT_TO_PROCESS_MS).subscribe(() => {
      this.advance(request, 'in-process', `${company.name} tomó la solicitud y la está procesando.`);
    }));
    this.timers.add(timer(ISSUANCE_TO_OUTCOME_MS).subscribe(() => this.finishIssuance(request, quote, n)));
    return request;
  }

  private advance(request: IssuanceRequest, status: IssuanceStatus, message: string): void {
    request.status = status;
    request.history.push({ date: this.today(), status, message });
  }

  private finishIssuance(request: IssuanceRequest, quote: Quote, n: number): void {
    const company = this.companyName(request.companyOid);
    if (n % 10 === 0) {
      request.rejectionReason = 'Riesgo fuera de la política de suscripción de la compañía.';
      this.advance(request, 'rejected', `${company} rechazó la solicitud: ${request.rejectionReason}`);
      quote.status = 'presented';
      this.log(quote.oid, `${company} rechazó la emisión: ${request.rejectionReason}`);
      this.notify(request.requestedByOid, 'issuance', 'Emisión rechazada',
        `La compañía rechazó la solicitud de ${quote.number}.`, 'quote', quote.oid);
    } else if (n % 5 === 0) {
      this.advance(request, 'pending-underwriting', `${company} pidió una revisión de suscripción antes de emitir.`);
      this.timers.add(timer(ISSUANCE_TO_OUTCOME_MS).subscribe(() => this.completeIssuance(request, quote)));
    } else {
      this.completeIssuance(request, quote);
    }
  }

  private completeIssuance(request: IssuanceRequest, quote: Quote): void {
    const today = this.today();
    const startDate = String(request.data['startDate'] || today);
    const subject = String(quote.risk['vehicle'] ?? quote.risk['description'] ?? 'Riesgo cotizado');
    const plate = request.data['plate'] || quote.risk['plate'];
    const policy: Policy = {
      oid: `pol-${++this.sequence}`,
      number: `${LINE_PREFIX[quote.lineOfBusiness]}-${String(this.runtimeRng.int(0, 999999)).padStart(6, '0')}`,
      lineOfBusiness: quote.lineOfBusiness, companyOid: request.companyOid, personOid: quote.personOid,
      ownerOid: quote.ownerOid, origin: 'platform', status: 'active', startDate,
      endDate: addDays(startDate, 365), premium: request.premium,
      subject: plate ? `${subject} (${plate})` : subject,
      lastSyncDate: today, divergent: false, renewalOfOid: quote.renewalOfPolicyOid ?? null, installmentsLoaded: true,
      licenseHolderOid: request.licenseHolderOid, attributedToOid: quote.ownerOid, issuanceOid: request.oid,
      versions: [initialVersion(startDate, request.premium, plate ? `${subject} (${plate})` : subject)],
    };
    this.db.policies.unshift(policy);
    const count = 6;
    const method = request.data['paymentMethod'];
    const installments: Installment[] = Array.from({ length: count }, (_, i) => ({
      oid: `ins-${policy.oid.slice(4)}-${String(i + 1).padStart(2, '0')}`,
      policyOid: policy.oid, ownerOid: policy.ownerOid, number: i + 1,
      dueDate: addDays(startDate, i * 60), amount: Math.round(policy.premium / count),
      status: 'pending', paidDate: null,
      paymentMethod: method ? { state: 'known', value: String(method) } : { state: 'pending' },
    }));
    this.db.installments.push(...installments);
    this.advance(request, 'issued', `${this.companyName(request.companyOid)} emitió la póliza ${policy.number}.`);
    request.policyOid = policy.oid;
    this.closeSale(quote, policy, `Póliza ${policy.number} emitida con ${this.companyName(request.companyOid)}`);
    this.notifyIssued(request, quote, policy);
  }

  registerManualPolicy(quoteOid: string, body: ManualPolicyBody): Policy | null {
    const user = this.currentUser();
    const quote = this.quoteFor(quoteOid);
    const company = this.db.companies.find(c => c.oid === body.companyOid);
    const member = this.db.members.find(m => m.oid === user?.memberOid);
    const offer = quote?.offers.find(o => o.companyOid === body.companyOid && o.status === 'received');
    if (!user || !quote || !company || !member || !offer || ['issued', 'desisted'].includes(quote.status)) return null;
    if (!this.tree_.availability(this.licenseHolderFor(member).oid, company.oid, quote.lineOfBusiness).ok) return null;
    const policy: Policy = {
      oid: `pol-${++this.sequence}`, number: body.number.trim(), lineOfBusiness: quote.lineOfBusiness,
      companyOid: company.oid, personOid: quote.personOid, ownerOid: quote.ownerOid, origin: 'manual',
      status: 'active', startDate: body.startDate, endDate: body.endDate, premium: body.premium,
      subject: String(quote.risk['vehicle'] ?? quote.risk['description'] ?? 'Riesgo cotizado'),
      lastSyncDate: this.today(), divergent: false, renewalOfOid: quote.renewalOfPolicyOid ?? null, installmentsLoaded: false,
      licenseHolderOid: this.licenseHolderFor(member).oid, attributedToOid: quote.ownerOid,
      versions: [initialVersion(body.startDate, body.premium, String(quote.risk['vehicle'] ?? quote.risk['description'] ?? 'Riesgo cotizado'))],
    };
    this.db.policies.unshift(policy);
    this.closeSale(quote, policy, `Póliza ${policy.number} registrada a mano (emitida en el sitio de ${company.name})`);
    this.notify(user.memberOid ?? quote.ownerOid, 'issuance', 'Póliza registrada', `Se registró la póliza ${policy.number}.`, 'policy', policy.oid);
    return policy;
  }

  private closeSale(quote: Quote, policy: Policy, text: string): void {
    quote.status = 'issued';
    quote.chosenCompanyOid = policy.companyOid;
    const person = this.db.persons.find(p => p.oid === quote.personOid);
    if (person) person.kind = 'client';
    const ticket = this.ensureSaleTicket(quote, quote.ownerOid);
    ticket.status = 'closed';
    ticket.category = 'trace';
    ticket.dueDate = null;
    ticket.relatedType = 'policy';
    ticket.relatedOid = policy.oid;
    ticket.title = `Venta ${policy.number}`;
    (ticket.events ??= []).push({ date: this.today(), text });
    ticket.lastActivityDate = this.today();
  }

  private closeTicket(quote: Quote, text: string): void {
    const ticket = this.ensureSaleTicket(quote, quote.ownerOid);
    ticket.status = 'closed';
    ticket.category = 'trace';
    ticket.dueDate = null;
    (ticket.events ??= []).push({ date: this.today(), text });
    ticket.lastActivityDate = this.today();
  }

  private notifyIssued(request: IssuanceRequest, quote: Quote, policy: Policy): void {
    const recipients = new Set([request.requestedByOid, quote.ownerOid]);
    recipients.forEach(oid => this.notify(oid, 'issuance', 'Póliza emitida', `Se emitió la póliza ${policy.number}.`, 'policy', policy.oid));
  }

  // ── Policy lifecycle: as-of, sync, renewals (R-03, R-04, R-08) ──

  policyAsOf(oid: string, date: string): PolicyAsOf | null {
    const policy = this.visible('policies').find(p => p.oid === oid) as Policy | undefined;
    if (!policy) return null;
    const ctx = this.context();
    const version = [...policy.versions].filter(v => v.effectDate <= date).sort((a, b) => b.version - a.version)[0] ?? null;
    const endDate = version?.endDate ?? policy.endDate;
    const status = version?.status === 'cancelled' ? 'cancelled'
      : date < policy.startDate ? 'pending' : date > endDate ? 'expired' : 'active';
    const unpaid = (ctx.installmentsByPolicy.get(policy.oid) ?? [])
      .filter(i => i.dueDate <= date && (i.paidDate === null || i.paidDate > date)).length;
    const age = daysBetween(policy.lastSyncDate, this.today());
    const warnings: PolicyWarning[] = [];
    if (status === 'cancelled') warnings.push({ kind: 'cancelled', text: 'La póliza estaba anulada o rescindida a esa fecha.' });
    else if (status !== 'active') warnings.push({ kind: 'out-of-term', text: 'La fecha queda fuera de la vigencia de la póliza.' });
    if (unpaid > 0) warnings.push({ kind: 'unpaid-installments', text: `Tenía ${unpaid} cuota(s) impaga(s) a esa fecha.` });
    if (policy.suspension && policy.suspension.date <= date) {
      warnings.push({ kind: 'suspended', text: 'La compañía informó la suspensión de la cobertura.' });
    }
    if (policy.divergent) warnings.push({ kind: 'divergent', text: 'La póliza es divergente: la compañía informa un estado distinto.' });
    if (age > SYNC_STALE_DAYS[policy.lineOfBusiness]) {
      warnings.push({ kind: 'stale-sync', text: `El dato está desactualizado: última sincronización hace ${age} días.` });
    }
    return {
      policyOid: policy.oid, policyNumber: policy.number, date, inTerm: status === 'active', status, version,
      totalVersions: policy.versions.length,
      endorsementsApplied: this.db.endorsements
        .filter(e => e.policyOid === policy.oid && (e.status === 'confirmed' || e.status === 'registered') && e.effectDate <= date)
        .map(e => projectRow('endorsements', e, ctx) as EndorsementRow),
      unpaidInstallments: unpaid, warnings,
    };
  }

  syncPolicy(oid: string): Outcome<SyncResult> {
    const policy = this.visible('policies').find(p => p.oid === oid) as Policy | undefined;
    if (!policy) return failure(404, 'NOT_FOUND', 'Póliza inexistente');
    policy.lastSyncDate = this.today();
    if (policy.divergent) {
      return { value: { divergedNow: false, message: 'La compañía sigue informando un estado distinto. La conciliación sigue abierta.' } };
    }
    if (policy.companyOid === COMPANY.pampa && this.pampaSyncs++ % 2 === 0) {
      policy.divergent = true;
      const me = this.currentUser()?.memberOid ?? policy.ownerOid;
      this.db.tickets.unshift({
        oid: `tck-${++this.sequence}`, number: `TK-${String(this.sequence).padStart(4, '0')}`, kind: 'reconciliation',
        category: 'task', title: `Conciliar póliza ${policy.number}`, status: 'open', assigneeOid: me, creatorOid: me,
        ownerOid: policy.ownerOid, dueDate: addDays(this.today(), 7), escalated: false, relatedType: 'policy',
        relatedOid: policy.oid, personOid: policy.personOid, lastActivityDate: this.today(),
        events: [{ date: this.today(), text: 'La compañía devolvió un estado distinto y sin el listado de endosos.' }],
      });
      this.notify(policy.ownerOid, 'divergence', `Póliza divergente — ${policy.number}`,
        'La compañía informa un estado distinto. Se abrió un ticket de conciliación.', 'policy', policy.oid);
      return {
        value: {
          divergedNow: true,
          message: 'La compañía devolvió un estado distinto y sin el listado de endosos: la póliza quedó divergente y se abrió el ticket de conciliación.',
        },
      };
    }
    return { value: { divergedNow: false, message: 'Póliza sincronizada: sin diferencias con lo que informa la compañía.' } };
  }

  requestCertificate(oid: string): Outcome<PolicyDocument> {
    const policy = this.visible('policies').find(p => p.oid === oid) as Policy | undefined;
    if (!policy) return failure(404, 'NOT_FOUND', 'Póliza inexistente');
    if (this.db.companies.find(c => c.oid === policy.companyOid)?.capabilities.documents !== 'api') {
      return failure(409, 'CERTIFICATE_MANUAL', 'La compañía no expone el certificado: pedilo por fuera y cargalo como documento.');
    }
    return { value: this.addDocument(policy, `Certificado de cobertura ${policy.number}.pdf`, 'certificate', 'from-company') };
  }

  createRenewalQuote(policyOid: string): Outcome<Quote> {
    const user = this.currentUser();
    const policy = this.visible('policies').find(p => p.oid === policyOid) as Policy | undefined;
    if (!user?.memberOid || !policy) return failure(404, 'NOT_FOUND', 'Póliza inexistente');
    const open = this.db.quotes.find(q => q.renewalOfPolicyOid === policy.oid && !['issued', 'desisted'].includes(q.status));
    if (open) return { value: open };
    const quote = this.createQuote({
      lineOfBusiness: policy.lineOfBusiness, personOid: policy.personOid, risk: riskFromSubject(policy), renewalOfPolicyOid: policy.oid,
    });
    return quote ? { value: quote } : failure(400, 'QUOTE_INVALID', 'Ninguna compañía habilitada cotiza este ramo');
  }

  registerSelfRenewal(policyOid: string, body: SelfRenewalBody): Outcome<Policy> {
    const previous = this.visible('policies').find(p => p.oid === policyOid) as Policy | undefined;
    if (!previous) return failure(404, 'NOT_FOUND', 'Póliza inexistente');
    if (this.db.policies.some(p => p.renewalOfOid === previous.oid)) {
      return failure(409, 'ALREADY_RENEWED', 'La póliza ya tiene una renovación encadenada');
    }
    const company = this.db.companies.find(c => c.oid === previous.companyOid);
    const premium = body.premium ?? Math.round((previous.premium * (1 + SELF_RENEWAL_PREMIUM_RISE_PCT / 100)) / 100) * 100;
    const policy: Policy = {
      ...previous,
      oid: `pol-${++this.sequence}`,
      number: body.number?.trim() || `${LINE_PREFIX[previous.lineOfBusiness]}-${String(this.runtimeRng.int(0, 999999)).padStart(6, '0')}`,
      origin: company?.capabilities.queryPolicies === 'api' ? 'api-import' : 'manual',
      status: 'active', startDate: previous.endDate, endDate: addDays(previous.endDate, 365), premium,
      lastSyncDate: this.today(), divergent: false, renewalOfOid: previous.oid, installmentsLoaded: false,
      notRenewing: undefined, suspension: undefined, issuanceOid: undefined,
      versions: [initialVersion(previous.endDate, premium, previous.subject)],
    };
    this.db.policies.unshift(policy);
    return { value: policy };
  }

  setNotRenewing(policyOid: string, reason: string): Outcome<Policy> {
    const policy = this.visible('policies').find(p => p.oid === policyOid) as Policy | undefined;
    if (!policy) return failure(404, 'NOT_FOUND', 'Póliza inexistente');
    policy.notRenewing = reason.trim() ? { reason: reason.trim(), date: this.today() } : undefined;
    return { value: policy };
  }

  // ── P-50: endorsements ────────────────────────────────────────

  createEndorsement(body: EndorsementBody): Outcome<Endorsement> {
    const user = this.currentUser();
    const policy = this.visible('policies').find(p => p.oid === body.policyOid) as Policy | undefined;
    const def = ENDORSEMENT_TYPES.find(t => t.type === body.type);
    const company = this.db.companies.find(c => c.oid === policy?.companyOid);
    if (!user?.memberOid || !policy || !def || !company) return failure(404, 'NOT_FOUND', 'Póliza inexistente');
    if (user.role === 'seller' && ENDING_TYPES.includes(body.type)) {
      return failure(403, 'ENDORSEMENT_FORBIDDEN', 'Un vendedor no puede anular ni rescindir pólizas');
    }
    if (!body.effectDate) return failure(400, 'ENDORSEMENT_INVALID', 'Falta la fecha de efecto');
    const route = body.type === 'free' ? 'free' : company.capabilities.endorse === 'api' ? 'api' : 'manual';
    if (route === 'free') {
      if (body.affectsMoney) {
        return failure(400, 'ENDORSEMENT_FREE_MONETARY', 'Un endoso libre no puede afectar prima, sumas, franquicias, coberturas ni ítems');
      }
      if (!body.description?.trim() || !body.documentName) {
        return failure(400, 'ENDORSEMENT_INVALID', 'El endoso libre necesita descripción y documento');
      }
    }
    if (route === 'manual' && (!body.issueDate || !body.companyNumber?.trim() || body.premiumDelta === undefined || !body.documentName)) {
      return failure(400, 'ENDORSEMENT_INVALID', 'Faltan la fecha de emisión, el número de endoso, el diferencial de prima o el documento');
    }
    const today = this.today();
    const index = this.db.endorsements.length + 1;
    const endorsement: Endorsement = {
      oid: `end-${String(index).padStart(4, '0')}`, number: `END-${String(index).padStart(4, '0')}`,
      policyOid: policy.oid, ownerOid: policy.ownerOid, type: body.type, status: route === 'api' ? 'sent' : 'registered',
      route, origin: route === 'api' ? 'platform' : 'manual', effectDate: body.effectDate,
      issueDate: route === 'manual' ? body.issueDate ?? null : null, registerDate: today,
      companyNumber: route === 'manual' ? body.companyNumber?.trim() ?? null : null,
      premiumDelta: route === 'manual' ? body.premiumDelta ?? null : null,
      description: body.description?.trim() ?? '', details: body.details ?? {},
      documentOid: null, ticketOid: null, resultingVersion: null, rejectionMessage: null,
    };
    if (body.documentName) {
      endorsement.documentOid = this.addDocument(policy, body.documentName, 'endorsement', 'uploaded').oid;
    }
    const ticket: Ticket = {
      oid: `tck-${++this.sequence}`, number: `TK-${String(this.sequence).padStart(4, '0')}`,
      kind: ENDING_TYPES.includes(body.type) ? 'cancellation' : 'endorsement', category: route === 'api' ? 'task' : 'trace',
      title: `${ENDORSEMENT_TYPE_LABELS[body.type]} en póliza ${policy.number}`,
      status: route === 'api' ? 'waiting-company' : 'resolved',
      assigneeOid: user.memberOid, creatorOid: user.memberOid, ownerOid: policy.ownerOid,
      dueDate: route === 'api' ? addDays(today, 5) : null, escalated: false, relatedType: 'policy', relatedOid: policy.oid,
      personOid: policy.personOid, lastActivityDate: today,
      events: [{
        date: today,
        text: route === 'api' ? `Endoso ${endorsement.number} enviado a ${company.name}` : `Endoso ${endorsement.number} registrado a mano`,
      }],
    };
    endorsement.ticketOid = ticket.oid;
    this.db.tickets.unshift(ticket);
    this.db.endorsements.unshift(endorsement);
    if (route === 'api') {
      const n = ++this.endorsementApiCount;
      this.timers.add(timer(ENDORSEMENT_CONFIRM_MS).subscribe(() => this.resolveEndorsement(endorsement, policy, ticket, company.name, n)));
    } else {
      this.applyEndorsement(endorsement, policy, endorsement.premiumDelta ?? 0);
      ticket.events?.push({ date: today, text: `Nueva versión ${endorsement.resultingVersion} de la póliza, con efecto ${endorsement.effectDate}` });
    }
    return { value: endorsement };
  }

  private resolveEndorsement(endorsement: Endorsement, policy: Policy, ticket: Ticket, companyName: string, n: number): void {
    const today = this.today();
    ticket.category = 'trace';
    ticket.dueDate = null;
    ticket.lastActivityDate = today;
    if (n % ENDORSEMENT_REJECT_EVERY === 0) {
      const message = 'ENDOSO_NO_ADMITIDO: la modificación solicitada no es compatible con las condiciones de la póliza';
      endorsement.status = 'rejected';
      endorsement.rejectionMessage = message;
      ticket.status = 'closed';
      ticket.events?.push({ date: today, text: `${companyName} rechazó el endoso: ${message}` });
      this.notify(ticket.assigneeOid, 'system', `Endoso rechazado — Póliza ${policy.number}`, message, 'policy', policy.oid);
      return;
    }
    const monetary = ENDORSEMENT_TYPES.find(t => t.type === endorsement.type)?.monetary && !ENDING_TYPES.includes(endorsement.type);
    const delta = monetary ? Math.round(this.runtimeRng.int(-5000, 25000) / 100) * 100 : 0;
    endorsement.status = 'confirmed';
    endorsement.issueDate = today;
    endorsement.companyNumber = `CE-${this.runtimeRng.int(10000, 99999)}`;
    endorsement.premiumDelta = delta;
    ticket.status = 'resolved';
    this.applyEndorsement(endorsement, policy, delta);
    ticket.events?.push({ date: today, text: `${companyName} confirmó el endoso: nueva versión ${endorsement.resultingVersion} de la póliza` });
    this.notify(ticket.assigneeOid, 'system', `Endoso confirmado — Póliza ${policy.number}`,
      `${companyName} confirmó el endoso ${endorsement.number}.`, 'policy', policy.oid);
  }

  private applyEndorsement(endorsement: Endorsement, policy: Policy, delta: number): void {
    const ending = ENDING_TYPES.includes(endorsement.type);
    if (ending) policy.status = 'cancelled';
    else policy.premium += delta;
    if (endorsement.type === 'risk-change' && endorsement.details['subject']) policy.subject = endorsement.details['subject'];
    if (endorsement.type === 'term-extension' && endorsement.details['endDate']) policy.endDate = endorsement.details['endDate'];
    const version = policy.versions.length + 1;
    policy.versions.push({
      version, effectDate: endorsement.effectDate, endorsementOid: endorsement.oid, premium: policy.premium,
      subject: policy.subject, status: ending ? 'cancelled' : 'active', endDate: policy.endDate,
    });
    endorsement.resultingVersion = version;
  }

  private addDocument(
    policy: Policy, name: string, kind: PolicyDocument['kind'], origin: PolicyDocument['origin'], claimOid: string | null = null,
  ): PolicyDocument {
    const document: PolicyDocument = {
      oid: `doc-${++this.sequence}`, name, kind, origin, date: this.today(), ownerOid: policy.ownerOid,
      personOid: policy.personOid, policyOid: policy.oid, claimOid,
      uploadedByOid: origin === 'uploaded' ? this.currentUser()?.memberOid ?? null : null, links: [],
    };
    this.db.documents.unshift(document);
    return document;
  }

  // ── P-61 / P-62: claims (R-05) ────────────────────────────────

  createClaim(body: ClaimBody): Outcome<Claim> {
    const user = this.currentUser();
    const policy = this.visible('policies').find(p => p.oid === body.policyOid) as Policy | undefined;
    const company = this.db.companies.find(c => c.oid === policy?.companyOid);
    if (!user?.memberOid || !policy || !company) return failure(404, 'NOT_FOUND', 'Póliza inexistente');
    const today = this.today();
    if (!body.incidentDate || body.incidentDate > today || !body.type || !body.description.trim()) {
      return failure(400, 'CLAIM_INVALID', 'Faltan la fecha del hecho (no futura), el tipo o la descripción');
    }
    const route = company.capabilities.reportClaim === 'api' ? 'api' : 'manual';
    if (route === 'manual' && (!body.companyClaimNumber?.trim() || !body.reportedDate)) {
      return failure(400, 'CLAIM_INVALID', 'Registrá el número de siniestro y la fecha de la denuncia hecha en la compañía');
    }
    if (route === 'api' && ++this.claimApiCount % CLAIM_REPORT_FAIL_EVERY === 0) {
      return failure(502, 'CLAIM_COMPANY_ERROR',
        `Timeout: ${company.name} no respondió a la denuncia. Mensaje original: "HTTP 504 Gateway Timeout". Reintentá.`);
    }
    const reportedDate = route === 'api' ? today : body.reportedDate ?? today;
    const claim: Claim = {
      oid: `clm-${String(this.db.claims.length + 1).padStart(3, '0')}`, number: `SIN-${this.runtimeRng.int(10000, 99999)}`,
      policyOid: policy.oid, personOid: policy.personOid, companyOid: policy.companyOid, ownerOid: policy.ownerOid,
      status: 'reported', incidentDate: body.incidentDate, reportedDate, type: body.type, description: body.description.trim(),
      legalDeadlineDate: addDays(reportedDate, CLAIM_RESPONSE_DAYS),
      companyClaimNumber: route === 'api' ? `CIA-${this.runtimeRng.int(10000, 99999)}` : body.companyClaimNumber?.trim() ?? '',
      lastUpdateDate: today, settledAmount: null, route, incidentTime: body.incidentTime || null, location: body.location.trim(),
      affectedItem: body.affectedItem.trim(), injured: body.injured, policeReport: body.policeReport,
      thirdParties: body.thirdParties.filter(t => t.name.trim()),
      checklist: pendingChecklist(body.type).map(item => {
        const received = body.receivedChecklist.includes(item.key);
        return { ...item, received, receivedDate: received ? today : null };
      }),
      history: [{
        date: today, status: 'reported', origin: route === 'api' ? 'company' : 'manual',
        text: route === 'api' ? `Denuncia enviada a ${company.name}` : `Denuncia registrada a mano (hecha en ${company.name})`,
      }],
      lastQueryDate: company.capabilities.queryClaims === 'api' ? today : null,
    };
    this.db.claims.unshift(claim);
    claim.checklist.filter(item => item.received).forEach(item => {
      this.addDocument(policy, `${item.label}.pdf`, 'claim', 'uploaded', claim.oid);
    });
    this.db.tickets.unshift({
      oid: `tck-${++this.sequence}`, number: `TK-${String(this.sequence).padStart(4, '0')}`, kind: 'claim', category: 'task',
      title: `Siniestro ${claim.number}`, status: 'in-progress', assigneeOid: user.memberOid, creatorOid: user.memberOid,
      ownerOid: policy.ownerOid, dueDate: claim.legalDeadlineDate, escalated: false, relatedType: 'claim', relatedOid: claim.oid,
      personOid: policy.personOid, lastActivityDate: today,
      events: [
        { date: today, text: `Siniestro ${claim.number} denunciado a ${company.name} (${route === 'api' ? 'por API' : 'registrado a mano'})` },
        { date: today, text: `Plazo para que la compañía se pronuncie: ${claim.legalDeadlineDate}` },
      ],
    });
    this.compliance.consumeDraft(body.draftOid, 'claim');
    return { value: claim };
  }

  changeClaimStatus(oid: string, body: ClaimStatusBody): Outcome<Claim> {
    const claim = this.visible('claims').find(c => c.oid === oid) as Claim | undefined;
    if (!claim) return failure(404, 'NOT_FOUND', 'Siniestro inexistente');
    const date = body.date || this.today();
    claim.status = body.status;
    claim.lastUpdateDate = date;
    claim.history.push({ date, status: body.status, origin: 'manual', text: body.comment.trim() || 'Cambio de estado cargado a mano' });
    return { value: claim };
  }

  refreshClaim(oid: string): Outcome<Claim> {
    const claim = this.visible('claims').find(c => c.oid === oid) as Claim | undefined;
    if (!claim) return failure(404, 'NOT_FOUND', 'Siniestro inexistente');
    if (this.db.companies.find(c => c.oid === claim.companyOid)?.capabilities.queryClaims !== 'api') {
      return failure(409, 'CLAIM_MANUAL', 'La compañía no permite consultar el estado: cambialo a mano');
    }
    const today = this.today();
    claim.lastQueryDate = today;
    const complete = claim.checklist.every(item => item.received);
    const next: ClaimStatus | null = claim.status === 'reported' ? (complete ? 'in-analysis' : 'documentation')
      : claim.status === 'documentation' && complete ? 'in-analysis' : null;
    if (next) {
      claim.status = next;
      claim.lastUpdateDate = today;
      claim.history.push({ date: today, status: next, origin: 'company', text: 'Estado actualizado por la compañía' });
    }
    return { value: claim };
  }

  receiveClaimDocument(oid: string, key: string): Outcome<Claim> {
    const claim = this.visible('claims').find(c => c.oid === oid) as Claim | undefined;
    const item = claim?.checklist.find(i => i.key === key);
    const policy = this.db.policies.find(p => p.oid === claim?.policyOid);
    if (!claim || !item || !policy) return failure(404, 'NOT_FOUND', 'Documento inexistente');
    item.received = true;
    item.receivedDate = this.today();
    this.addDocument(policy, `${item.label}.pdf`, 'claim', 'uploaded', claim.oid);
    claim.history.push({ date: this.today(), status: claim.status, origin: 'platform', text: `Documento recibido: ${item.label}` });
    return { value: claim };
  }

  // ── P-79: client communications (R-06) ────────────────────────

  createCommunication(body: CommunicationBody): Outcome<Communication> {
    const user = this.currentUser();
    const person = this.visible('persons').find(p => p.oid === body.personOid) as Person | undefined;
    const template = templateByKey(body.templateKey);
    if (!user?.memberOid || !person || !template) return failure(404, 'NOT_FOUND', 'Cliente o plantilla inexistente');
    if (person.email.state !== 'known' || !person.email.value) {
      return failure(400, 'COMMUNICATION_NO_EMAIL', 'El cliente no tiene mail informado');
    }
    if (template.kind === 'commercial' && person.commercialOptOut) {
      return failure(400, 'COMMUNICATION_OPT_OUT', 'El cliente se dio de baja de las comunicaciones comerciales');
    }
    if (!body.subject.trim() || !body.body.trim()) return failure(400, 'COMMUNICATION_INVALID', 'Falta el asunto o el texto');
    const communication: Communication = {
      oid: `com-${++this.sequence}`, ownerOid: person.ownerOid, personOid: person.oid, templateKey: template.key, kind: template.kind,
      channel: 'email', to: person.email.value, subject: body.subject.trim(), body: body.body.trim(),
      attachments: this.db.documents.filter(d => body.attachmentOids.includes(d.oid)).map(d => d.name),
      date: this.today(), sentByOid: user.memberOid, relatedType: body.relatedType ?? null, relatedOid: body.relatedOid ?? null,
    };
    this.db.communications.unshift(communication);
    return { value: communication };
  }

  // ── Offer simulation helpers ──────────────────────────────────

  private quoteFor(oid: string): Quote | undefined {
    return (this.visible('quotes') as unknown as Quote[]).find(q => q.oid === oid);
  }

  private licenseHolderFor(member: TreeMember): TreeMember {
    return member.kind === 'producer' ? member
      : this.db.members.find(m => m.oid === member.parentOid) ?? member;
  }

  private memberName(oid: string): string {
    return this.db.members.find(m => m.oid === oid)?.name ?? '—';
  }

  private companyName(oid: string): string {
    return this.db.companies.find(c => c.oid === oid)?.name ?? oid;
  }

  private ensureSaleTicket(quote: Quote, assigneeOid: string): Ticket {
    const existing = this.db.tickets.find(t => t.quoteOid === quote.oid);
    if (existing) return existing;
    const ticket: Ticket = {
      oid: `tck-${++this.sequence}`, number: `TK-${String(this.sequence).padStart(4, '0')}`,
      kind: 'sale', category: 'trace', title: `Venta ${quote.number}`, status: 'in-progress',
      assigneeOid, creatorOid: quote.ownerOid, ownerOid: quote.ownerOid,
      dueDate: null, escalated: false, relatedType: 'quote', relatedOid: quote.oid, personOid: quote.personOid,
      lastActivityDate: this.today(), quoteOid: quote.oid, events: [],
    };
    this.db.tickets.unshift(ticket);
    return ticket;
  }

  private log(quoteOid: string, text: string): void {
    const ticket = this.db.tickets.find(t => t.quoteOid === quoteOid);
    if (!ticket) return;
    (ticket.events ??= []).push({ date: this.today(), text });
    ticket.lastActivityDate = this.today();
  }

  private pendingOffer(companyOid: string, attempt: number): QuoteOffer {
    return {
      companyOid, status: 'pending', attempt, premium: null, coverageLevel: null,
      deductible: null, respondedAt: null, message: null, companyMessage: null, errorCategory: null,
    };
  }

  private scheduleOffer(quote: Quote, offer: QuoteOffer): void {
    const company = this.db.companies.find(c => c.oid === offer.companyOid);
    if (!company) return;
    const [min, max] = company.quoteDelayMs;
    this.timers.add(timer(this.runtimeRng.int(min, max)).subscribe(() => this.resolveOffer(quote, offer)));
  }

  private resolveOffer(quote: Quote, offer: QuoteOffer): void {
    if (!quote.offers.includes(offer)) return;
    const line = quote.lineOfBusiness;
    const vehicleYear = Number(quote.risk['vehicleYear'] ?? 0);
    const oldCar = line === 'auto' && vehicleYear > 0
      && new Date(`${this.today()}T12:00:00Z`).getUTCFullYear() - vehicleYear > 15;
    offer.respondedAt = this.today();
    if (offer.companyOid === COMPANY.federal && offer.attempt === 1) {
      offer.status = 'timeout';
      offer.message = 'La compañía no respondió a tiempo. Reintentá.';
      offer.companyMessage = 'HTTP 504 Gateway Timeout: el cotizador de Federal Andina no respondió en 30 s';
      offer.errorCategory = 'Timeout';
    } else if (offer.companyOid === COMPANY.nexo && oldCar) {
      offer.status = 'no-offer';
      offer.message = 'La compañía no cotiza este riesgo.';
      offer.companyMessage = 'RIESGO_NO_ADMITIDO: antigüedad del vehículo superior a 15 años';
      offer.errorCategory = 'Riesgo no admitido';
    } else {
      this.fillOffer(quote, offer);
    }
    if (quote.offers.every(o => o.status !== 'pending')) {
      const received = quote.offers.filter(o => o.status === 'received').length;
      this.log(quote.oid, `Ofertas recibidas: ${received} de ${quote.offers.length} (versión ${quote.version})`);
    }
  }

  private fillOffer(quote: Quote, offer: QuoteOffer): void {
    const line = quote.lineOfBusiness;
    const levels = COVERAGE_LEVELS[line];
    const level = this.runtimeRng.pick(levels);
    const index = levelIndex(line, level);
    const [min, max] = PREMIUM_RANGE[line];
    const levelFactor = levels.length > 1 ? 0.75 + (0.5 * index) / (levels.length - 1) : 1;
    const tier = PRODUCT_TIER[offer.companyOid] ?? 'Base';
    const sum = Number(quote.risk[SUM_INSURED_KEY[line]]);
    offer.status = 'received';
    offer.message = null;
    offer.companyMessage = null;
    offer.errorCategory = null;
    offer.premium = Math.round((this.runtimeRng.int(min, max) * levelFactor) / 100) * 100;
    offer.coverageLevel = level;
    offer.product = `${LINE_LABELS[line]} ${tier}`;
    offer.planName = `${tier} ${level}`;
    offer.sumInsured = Number.isFinite(sum) && sum > 0 && !(line === 'auto' && index === 0) ? sum : null;
    offer.installmentsText = this.runtimeRng.pick(INSTALLMENT_TEXTS);
    offer.deductible = line === 'auto' && index > 0
      ? (index === 2 ? this.runtimeRng.pick([ADJUST_DEDUCTIBLE_STEP, ADJUST_DEDUCTIBLE_STEP * 2, ADJUST_DEDUCTIBLE_STEP * 3]) : 0)
      : null;
  }

  // ── Time-based events (M-10) ──────────────────────────────────

  private fireTimeBasedEvents(): void {
    const today = this.today();
    const parentOf = (oid: string) => this.db.members.find(m => m.oid === oid)?.parentOid ?? oid;

    for (const ticket of this.db.tickets) {
      if (ticket.escalated || !ticket.dueDate || ticket.dueDate >= today || !ACTIVE_TICKET.includes(ticket.status)) continue;
      ticket.escalated = true;
      ticket.assigneeOid = parentOf(ticket.assigneeOid);
      ticket.lastActivityDate = today;
      this.notify(ticket.assigneeOid, 'ticket-escalated', `Ticket escalado: ${ticket.number}`, ticket.title, null, null);
    }
    for (const installment of this.db.installments) {
      if (installment.status !== 'pending' || installment.dueDate >= today) continue;
      installment.status = 'overdue';
      const policy = this.db.policies.find(p => p.oid === installment.policyOid);
      this.notify(installment.ownerOid, 'overdue-installment', `Cuota ${installment.number} vencida — Póliza ${policy?.number ?? ''}`, 'Podés avisarle al cliente con una plantilla.', 'policy', installment.policyOid);
    }
    for (const installment of this.db.installments) {
      const policy = this.db.policies.find(p => p.oid === installment.policyOid);
      if (!policy || policy.suspension || installment.status !== 'overdue' || daysBetween(installment.dueDate, today) <= MORA_DAYS) continue;
      if (this.db.companies.find(c => c.oid === policy.companyOid)?.capabilities.queryPolicies !== 'api') continue;
      policy.suspension = { date: today };
      this.notify(policy.ownerOid, 'suspension', `Suspensión informada por la compañía — Póliza ${policy.number}`,
        'La plataforma no suspende por su cuenta: es la compañía la que lo informa.', 'policy', policy.oid);
    }
    for (const claim of this.db.claims) {
      const key = `claim-deadline:${claim.oid}`;
      if (this.notified.has(key) || ['accepted', 'rejected', 'closed'].includes(claim.status)) continue;
      if (claim.legalDeadlineDate <= addDays(today, CLAIM_ALERT_DAYS)) {
        this.notified.add(key);
        this.notify(claim.ownerOid, 'claim-deadline', `Plazo legal por vencer — Siniestro ${claim.number}`, 'Pasado el plazo de 30 días, el silencio de la compañía importa aceptación.', 'claim', claim.oid);
      }
    }
    for (const policy of this.db.policies) {
      const key = `renewal:${policy.oid}`;
      if (this.notified.has(key) || policy.status !== 'active') continue;
      if (policy.endDate >= today && policy.endDate <= addDays(today, 30)) {
        this.notified.add(key);
        this.notify(policy.ownerOid, 'renewal', `Renueva pronto — Póliza ${policy.number}`, 'Recotizá el mercado habilitado.', 'policy', policy.oid);
      }
    }
  }

  private notify(
    recipientOid: string, kind: ProtoNotification['kind'], title: string, body: string,
    relatedType: ProtoNotification['relatedType'], relatedOid: string | null,
  ): void {
    this.db.notifications.unshift({
      oid: `ntf-${++this.sequence}`, recipientOid, kind, title, body,
      createDate: this.today(), read: false, relatedType, relatedOid,
    });
  }

  // ── Scoping ───────────────────────────────────────────────────

  private visible(name: CollectionName): Record_[] {
    const items = this.db[COLLECTION_KEYS[name]] as unknown as Record_[];
    const user = this.currentUser();
    if (!user?.memberOid) return [];
    if (name === 'notifications') return items.filter(i => i.recipientOid === user.memberOid);
    const owners = user.role === 'seller' ? new Set([user.memberOid]) : this.subtree(user.memberOid);
    const treeOf = new Map(this.db.members.map(m => [m.oid, m.treeOid]));
    return items.filter(i => i.ownerOid !== undefined && owners.has(i.ownerOid) && treeOf.get(i.ownerOid) === user.treeOid);
  }

  private subtree(rootOid: string): Set<string> {
    const result = new Set([rootOid]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const m of this.db.members) {
        if (m.parentOid && result.has(m.parentOid) && !result.has(m.oid)) {
          result.add(m.oid);
          grew = true;
        }
      }
    }
    return result;
  }
}
